/* eslint-disable comma-dangle */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  CheckOutlined,
  CloudServerOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  RedoOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  List,
  Input,
  Card,
  Avatar,
  Tabs,
  Badge,
  Dropdown,
  Modal,
  Typography,
  Button,
  Space,
  Tooltip,
  message,
} from 'antd';
import { debounce } from 'lodash';
import { getAppList, getLoading } from '@store/admin/apps/selectors';
import {
  apiKeyLimitFeatureSelector,
  pulse5ApiUsageDashboardFeatureSelector,
  showApiUsageBreakdownFeatureSelector,
} from '@store/common/selectors';
import { checkCanEditApiManagement } from '@src/globalStore/profileStore';
import { list, update, del } from '@store/admin/apps/actions';
import PageHeader from '@components/common/PageHeader';
import { singleAppPrefix } from '@src/routers/utils';
import HelpRedirect from '@src/components/common/HelpRedirect';
import { API_MANAGEMENT_HELP } from '@src/utils/help/links';

const { TabPane } = Tabs;
const { Search } = Input;
const { confirm } = Modal;
const { Paragraph } = Typography;

const ApplicationListItem = ({ app, canEditApiManagement }) => {
  const dispatch = useDispatch();

  const confirmDelete = (appToBeDeleted) => {
    confirm({
      title: 'Do you want to delete this application?',
      content: appToBeDeleted.title,
      onOk() {
        dispatch(del(appToBeDeleted));
      },
    });
  };

  const onUpdated = (err) => {
    if (err) {
      message.error(err.message);
    }
  };

  const onActivate = () => {
    dispatch(update({ ...app, inactive: false }, onUpdated));
  };

  const onDeactivate = () => {
    dispatch(update({ ...app, inactive: true }, onUpdated));
  };

  const onAppDelete = () => {
    confirmDelete(app);
  };

  return (
    <List.Item
      key={app.clientId}
      actions={[
        // add username || email, so that it won't be empty
        <Paragraph type="secondary" key="para-one">
          {`updated at ${new Date(
            app.updated_at,
          ).toLocaleDateString()} by ${app.updatedBy.email || app.updatedBy.username} `}
        </Paragraph>,
        <Paragraph type="secondary" key="para-two">
          {`created at ${new Date(
            app.created_at,
          ).toLocaleDateString()} by ${app.createdBy.email || app.createdBy.username} `}
        </Paragraph>,
      ]}
      extra={
        <Dropdown
          key="1"
          menu={{
            items: canEditApiManagement
              ? [
                  {
                    key: 'edit',
                    label: (
                      <Link to={`${singleAppPrefix}/manage/apps/detail/${app.clientId}`}>
                        <Space>
                          <EditOutlined />
                          <span>Edit</span>
                        </Space>
                      </Link>
                    ),
                  },
                  ...(app.inactive
                    ? [
                        {
                          key: 'activate',
                          label: (
                            <div onClick={onActivate}>
                              <Space>
                                <CheckOutlined />
                                <span>Activate</span>
                              </Space>
                            </div>
                          ),
                        },
                      ]
                    : [
                        {
                          key: 'deactivate',
                          label: (
                            <div onClick={onDeactivate}>
                              <Space>
                                <StopOutlined />
                                <span>Deactivate</span>
                              </Space>
                            </div>
                          ),
                        },
                      ]),
                  {
                    key: 'delete',
                    label: (
                      <div onClick={onAppDelete}>
                        <Space>
                          <DeleteOutlined />
                          <span>Delete</span>
                        </Space>
                      </div>
                    ),
                  },
                ]
              : [
                  {
                    key: 'view',
                    label: (
                      <Link to={`${singleAppPrefix}/manage/apps/detail/${app.clientId}`}>
                        <Space>
                          <EyeOutlined />
                          <span>View</span>
                        </Space>
                      </Link>
                    ),
                  },
                ],
          }}
        >
          <Button>
            <MoreOutlined />
          </Button>
        </Dropdown>
      }
    >
      <List.Item.Meta
        avatar={<Avatar size="large" icon={<CloudServerOutlined />} />}
        title={
          <Link to={`${singleAppPrefix}/manage/apps/detail/${app.clientId}`}>{app.title}</Link>
        }
        description={app.description}
      />
      <Paragraph>{`Client ID: ${app.clientId}`}</Paragraph>
    </List.Item>
  );
};

const ApplicationList = () => {
  const dispatch = useDispatch();
  const apps = useSelector(getAppList);
  const loading = useSelector(getLoading);

  const refresh = () => {
    dispatch(list());
  };

  useEffect(() => {
    if (!loading && !apps) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filter, setFilter] = useState('');
  const onSearchChange = debounce(setFilter, 200);
  const isPulseAPIUsageEnabled = useSelector(pulse5ApiUsageDashboardFeatureSelector);
  const showApiUsageBreakdown = useSelector(showApiUsageBreakdownFeatureSelector);
  const appLimit = useSelector(apiKeyLimitFeatureSelector) ?? 50;
  const canEditApiManagement = checkCanEditApiManagement();

  if (loading || !apps) {
    return null;
  }
  const filteredApps = filter
    ? apps.filter(
        (app) =>
          app.title.toLowerCase().includes(filter) || app.clientId.toLowerCase().includes(filter),
      )
    : apps;
  const activeApps = filteredApps.filter((app) => !app.inactive);
  const inactiveApps = filteredApps.filter((app) => app.inactive);

  const onSearch = (e) => {
    onSearchChange(e.target.value.toLowerCase());
  };

  const renderApp = (app) => (
    <ApplicationListItem app={app} canEditApiManagement={canEditApiManagement} />
  );

  const reachedLimit = activeApps.length >= appLimit;

  /* eslint-disable */
  return (
    <div>
      <PageHeader
        title="Applications"
        extra={[
          <HelpRedirect redirectUrl={API_MANAGEMENT_HELP} style={{ marginRight: '5px' }} />,
          canEditApiManagement &&
            (reachedLimit ? (
              <Tooltip
                key="limit-tooltip"
                title={`You have reached the limit of ${appLimit} applications.`}
              >
                <Button disabled={true}>Create New App</Button>
              </Tooltip>
            ) : (
              <Link key="1" to={`${singleAppPrefix}/manage/apps/create`}>
                <Button>Create New App</Button>
              </Link>
            )),

          isPulseAPIUsageEnabled && showApiUsageBreakdown && (
            <Button
              key="api-usage"
              onClick={() => window.dispatchEvent(new CustomEvent('open-api-usage-breakdown'))}
            >
              API Usage
            </Button>
          ),

          <Dropdown
            key="2"
            menu={{
              items: [
                {
                  key: 'refresh',
                  label: (
                    <div onClick={refresh}>
                      <Space>
                        <RedoOutlined />
                        <span>Refresh</span>
                      </Space>
                    </div>
                  ),
                },
              ],
            }}
          >
            <Button>
              <span>More</span>
              <DownOutlined />
            </Button>
          </Dropdown>,
        ]}
        style={{
          marginTop: '0',
          marginLeft: '1px',
          backgroundColor: 'white',
        }} // eslint-disable-line
      />
      <Card>
        <Search placeholder="Search by Name or Client ID" onChange={onSearch} />
        <Tabs defaultActiveKey="active" animated={false}>
          <TabPane
            tab={
              <Badge
                count={activeApps.length}
                style={{
                  backgroundColor: '#fff',
                  color: '#999',
                  boxShadow: '0 0 0 1px #d9d9d9 inset',
                }}
              >
                Active&nbsp;&nbsp;&nbsp;
              </Badge>
            }
            key="active"
          >
            <List itemLayout="vertical" dataSource={activeApps} renderItem={renderApp} />
          </TabPane>
          <TabPane
            tab={
              <Badge
                count={inactiveApps.length}
                style={{
                  backgroundColor: '#fff',
                  color: '#999',
                  boxShadow: '0 0 0 1px #d9d9d9 inset',
                }}
              >
                Inactive&nbsp;&nbsp;&nbsp;
              </Badge>
            }
            key="inactive"
          >
            <List itemLayout="vertical" dataSource={inactiveApps} renderItem={renderApp} />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};
/* eslint-enable */

export default ApplicationList;
